import {
  DiscountTwoTone,
  FilterAltTwoTone,
  HomeTwoTone,
  SearchTwoTone,
  ShareTwoTone,
  SortTwoTone,
  SwapVertTwoTone,
  TodayTwoTone,
} from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Box,
  Breadcrumbs,
  Button,
  Grid,
  IconButton,
  Link as MLink,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SearchOptionType, onlineTourSearchResultN } from '../api';
import { OnlineTourCard } from '../components/OnlineTourCard';
import OnlineToursSortingDialog from '../components/OnlineToursSortingDialog';
import PageHeader from '../components/PageHeader';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import {
  IOnlineTourSearchPropertyResult,
  IOnlineTourSearchResult,
} from '../utils/types';
import { FromAutoCompleteDialogContainer } from './FromAutoCompleteDialogContainer';
import OnlineToursFilterDialog from './OnlineToursFilterDialog';
import { FilterValues, OnlineToursFilters } from './OnlineToursFilters';

export default function OnlineToursContainer() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [queryParams, setQueryParams] = useSearchParams();
  const nav = useNavigate();
  const [control, setControl] = useState(false);
  const [selectedSearchTypeOption, setSelectedSearchOption] =
    useState<IOnlineTourSearchPropertyResult | null>(null);

  const [sortFilterDialogOpen, setSortFilterDialogOpen] = useState(false);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [onlineSearchResults, setOnlineSearchResults] = useState<
    IOnlineTourSearchResult[]
  >([]);

  const showFilter = queryParams.get('showFilter') !== 'false';
  const [tourTypeMobileFilterDialogOpen, setTourTypeMobileFilterDialogOpen] =
    useState(false);
  const qStartDate = queryParams.get('startDate');
  const qEndDate = queryParams.get('endDate');
  const filterValues = useMemo(
    () => ({
      categories: queryParams.getAll('categories').map(Number),
      tourDayCount: queryParams.getAll('tourDayCount').map(Number),
      tourConcept: queryParams.getAll('tourConcept').map(Number),
      departureCities: queryParams.getAll('departureCities').map(Number),
      departureAirports: queryParams.getAll('departureAirports').map(Number),
      accCities: queryParams.getAll('accCities').map(Number),
      months: queryParams.getAll('months').map(Number),
      startDate: qStartDate ? new Date(qStartDate) : null,
      endDate: qEndDate ? new Date(qEndDate) : null,
      tourProductTypes: queryParams.getAll('tourProductTypes').map(Number),
    }),
    [queryParams, qStartDate, qEndDate],
  );
  const sort = queryParams.get('sort') || 'startDate,asc';
  const page = parseInt(queryParams.get('page') || '0');
  const pageSize = parseInt(queryParams.get('pageSize') || '10');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tourUrl, setTourUrl] = useState<string>('');
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const showSnackbar = useAlertSnackbar();

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleShare = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);

    const queryString = queryParams.toString();
    setTourUrl(`https://hisarturizm.com.tr/turlar?${queryString}`);
  };
  const handleCopyLink = () => {
    navigator.clipboard.writeText(tourUrl);
    showSnackbar(t('guideCustomerOperationState.copied'), 'success');
    handleCloseMenu();
  };

  const handleShareWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(tourUrl)}`;
    window.open(whatsappUrl, '_blank');
    handleCloseMenu();
  };

  useEffect(() => {
    // fetch data when sorting changes
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, page, pageSize, sort]);

  const setQueryParam = (key: string, value: string) => {
    const newParams = new URLSearchParams(queryParams.toString());
    newParams.set(key, value);
    setQueryParams(newParams);
  };

  const handleSortFilterOpenDialog = () => {
    setSortFilterDialogOpen(true);
  };

  const handleSortFilterCloseDialog = () => {
    setSortFilterDialogOpen(false);
  };

  const handleTourTypeMobileFilterOpenDialog = () => {
    setTourTypeMobileFilterDialogOpen(true);
  };

  const handleTourTypeMobileFilterCloseDialog = () => {
    setTourTypeMobileFilterDialogOpen(false);
  };

  const handleChangePage = (newPage: number) => {
    setQueryParam('page', String(newPage));
    // reset scroll
    // todo: this shouldn't be necessary when updating search params with react-router
    window.scrollTo(0, 0);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newParams = new URLSearchParams(queryParams.toString());
    newParams.set('pageSize', event.target.value);
    newParams.set('page', '0');
    setQueryParams(newParams);
    window.scrollTo(0, 0);
  };

  const handleFilterValuesChange = (vals: FilterValues) => {
    const sp = new URLSearchParams();
    vals.categories.map((v) => sp.append('categories', String(v)));
    vals.tourDayCount.map((v) => sp.append('tourDayCount', String(v)));
    vals.tourConcept.map((v) => sp.append('tourConcept', String(v)));
    vals.departureCities.map((v) => sp.append('departureCities', String(v)));
    vals.months.map((v) => sp.append('months', String(v)));
    vals.startDate && sp.set('startDate', format(vals.startDate, 'yyyy-MM-dd'));
    vals.endDate && sp.set('endDate', format(vals.endDate, 'yyyy-MM-dd'));
    setQueryParams(sp);
  };

  const handleSearch = async () => {
    try {
      const {
        categories,
        tourDayCount,
        tourConcept,
        departureCities,
        departureAirports,
        accCities,
        months,
        startDate,
        endDate,
        tourProductTypes,
      } = filterValues;
      const selectedOptions: {
        type: SearchOptionType;
        propertyIds: string;
      }[] = [];

      if (categories.length > 0) {
        selectedOptions.push({
          type: SearchOptionType.CATEGORY,
          propertyIds: categories.join(','),
        });
      }
      if (tourDayCount.length > 0) {
        selectedOptions.push({
          type: SearchOptionType.TOUR_DAY_COUNT,
          propertyIds: tourDayCount.join(','),
        });
      }
      if (tourConcept.length > 0) {
        selectedOptions.push({
          type: SearchOptionType.TOUR_CONCEPT,
          propertyIds: tourConcept.join(','),
        });
      }
      if (departureCities.length > 0) {
        selectedOptions.push({
          type: SearchOptionType.DEPARTURE_CITIES,
          propertyIds: departureCities.join(','),
        });
      }
      if (departureAirports.length > 0) {
        selectedOptions.push({
          type: SearchOptionType.DEPARTURE_AIRPORTS,
          propertyIds: departureAirports.join(','),
        });
      }
      if (accCities.length > 0) {
        selectedOptions.push({
          type: SearchOptionType.ACC_CITIES,
          propertyIds: accCities.join(','),
        });
      }
      if (months.length > 0) {
        selectedOptions.push({
          type: SearchOptionType.START_DATE_MONTH,
          propertyIds: months.join(','),
        });
      }
      if (tourProductTypes.length > 0) {
        selectedOptions.push({
          type: SearchOptionType.TOUR_PRODUCT_TYPES,
          propertyIds: tourProductTypes.join(','),
        });
      }

      const res = await onlineTourSearchResultN(
        selectedOptions,
        startDate ? format(new Date(startDate), 'yyyy-MM-dd') : '',
        endDate ? format(new Date(endDate), 'yyyy-MM-dd') : '',
        page,
        pageSize,
        sort,
      );
      const totalCount = res.headers.get('X-Total-Count');
      const data = await res.json();
      if (res?.status === 200) {
        setOnlineSearchResults(data);
        setTotalResults(parseInt(totalCount || '0'));
      } else {
        throw data;
      }
    } catch (e: any) {
      setError(e.detail || 'An error occurred');
      console.log(error); //TODO
    } finally {
      setLoading(false);
    }
  };

  const handleSearchTypeOption = () => {
    const sp = new URLSearchParams();
    setControl(true);
    if (selectedSearchTypeOption) {
      switch (selectedSearchTypeOption.type) {
        case SearchOptionType.CATEGORY:
          sp.append('categories', selectedSearchTypeOption.id.toString());
          break;
        case SearchOptionType.TOUR_DAY_COUNT:
          sp.append('tourDayCount', selectedSearchTypeOption.id.toString());
          break;
        case SearchOptionType.TOUR_CONCEPT:
          sp.append('tourConcept', selectedSearchTypeOption.id.toString());
          break;
        case SearchOptionType.DEPARTURE_CITIES:
          sp.append('departureCities', selectedSearchTypeOption.id.toString());
          break;
        case SearchOptionType.DEPARTURE_AIRPORTS:
          sp.append(
            'departureAirports',
            selectedSearchTypeOption.id.toString(),
          );
          break;
        case SearchOptionType.ACC_CITIES:
          sp.append('accCities', selectedSearchTypeOption.id.toString());
          break;
        case SearchOptionType.TOUR_PRODUCT_TYPES:
          sp.append('tourProductTypes', selectedSearchTypeOption.id.toString());
          break;
      }
    }

    if (Array.from(sp).length > 0) {
      nav(`/tours?${sp.toString()}`);
      setSelectedSearchOption(null);
    } else {
      nav(`/tours`);
    }
  };

  useEffect(() => {
    if (selectedSearchTypeOption) {
      handleSearchTypeOption();
      setSelectedSearchOption(null);
    }
  }, [selectedSearchTypeOption]);

  return (
    <Box>
      {sortFilterDialogOpen && (
        <OnlineToursSortingDialog
          open={sortFilterDialogOpen}
          onClose={handleSortFilterCloseDialog}
        />
      )}
      {tourTypeMobileFilterDialogOpen && (
        <OnlineToursFilterDialog
          open={tourTypeMobileFilterDialogOpen}
          onClose={handleTourTypeMobileFilterCloseDialog}
          initialFilterValues={filterValues}
          onSearch={handleFilterValuesChange}
        />
      )}

      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2}>
        {!isMobile && showFilter && (
          <div>
            <Box sx={{ width: 240 }}>
              <Box
                sx={{
                  position: 'fixed',
                  top: 60,
                  width: 240,
                  bottom: 0,
                  overflow: 'auto',
                }}
              >
                <OnlineToursFilters
                  initialFilterValues={filterValues}
                  onSearch={handleFilterValuesChange}
                  control={control}
                  setControl={setControl}
                />
              </Box>
            </Box>
          </div>
        )}

        <Box>
          <PageHeader
            title={t('onlineToursPage.tours')}
            subtitle={`${t('onlineToursPage.order')}: ${
              {
                'availableCapacity,asc': t('onlineToursPage.lastQuotas'),
                'adultPriceUSD,asc': t('onlineToursPage.increasingByPrice'),
                'adultPriceUSD,desc': t('onlineToursPage.decreasingByPrice'),
                'startDate,asc': t('onlineToursPage.nearestDate'),
              }[sort]
            } - ${t('onlineToursPage.toursFounded', {
              count: totalResults,
            })}`}
            actions={
              <>
                <Box sx={{ display: 'inline-block' }}>
                  <IconButton
                    onClick={() => {
                      setShowSearchDialog(true);
                    }}
                  >
                    <SearchTwoTone />
                  </IconButton>
                  <IconButton onClick={(e) => handleShare(e)} sx={{ mr: 1 }}>
                    <ShareTwoTone />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                  >
                    <MenuItem onClick={handleCopyLink}>
                      <ContentCopyIcon color="primary" />
                      <Typography sx={{ ml: 1 }}>
                        {t('guideCustomerOperationState.copyLink')}
                      </Typography>
                    </MenuItem>
                    <MenuItem onClick={handleShareWhatsApp}>
                      <WhatsAppIcon color="primary" />
                      <Typography sx={{ ml: 1 }}>
                        {t('guideCustomerOperationState.shareInWhatsApp')}
                      </Typography>
                    </MenuItem>{' '}
                  </Menu>
                </Box>
              </>
            }
            sx={{ mb: 0 }}
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Breadcrumbs>
              <MLink
                component={Link}
                to="/"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <HomeTwoTone />
              </MLink>
              <Typography>{t('onlineToursPage.tours')}</Typography>
            </Breadcrumbs>

            <Box>
              <Button onClick={handleSortFilterOpenDialog} size="small">
                <SwapVertTwoTone />
                {t('onlineToursPage.order')}{' '}
              </Button>

              {isMobile && showFilter && (
                <Button
                  onClick={handleTourTypeMobileFilterOpenDialog}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <FilterAltTwoTone />
                  {t('onlineTourFiltering.Filter')}
                </Button>
              )}
            </Box>
          </Stack>

          {showSearchDialog && (
            <FromAutoCompleteDialogContainer
              value={selectedSearchTypeOption}
              onChange={setSelectedSearchOption}
              variant="outlined"
              onClose={() => setShowSearchDialog(false)}
              open
            />
          )}

          {onlineSearchResults?.map((r: IOnlineTourSearchResult) => (
            <Box key={r.id} sx={{ mb: 2 }}>
              <OnlineTourCard
                tourId={r.id}
                coverImg={
                  r.images.length > 0
                    ? r.images.find((img) => img.favorite)?.path ??
                      r.images[0].path
                    : null
                }
                flightCompanyInfo={
                  r?.flightCompanyInfo && r?.flightCompanyInfo[0]
                }
                tourName={r.name}
                tourCode={r.code}
                start={{
                  departureCity: r.departureDepartureCity,
                  departureAirport: r.departureDeparture,
                  arrivalCity: r.departureArrivalCity,
                  arrivalAirport: r.departureArrival,
                }}
                end={{
                  departureCity: r.arrivalDepartureCity,
                  departureAirport: r.arrivalDeparture,
                  arrivalCity: r.arrivalArrivalCity,
                  arrivalAirport: r.arrivalArrival,
                }}
                tourStartDate={r.departureDate}
                tourFinishDate={r.arrivalDate}
                night={r.dayCount - 1}
                day={r.dayCount}
                tourPrice={r.adultPrice}
                currencyCode={r.currencyCode}
                remainingTime={r.remainingDay}
                remainingSeat={r.availableCapacity}
                hideShareAndPrint
              />
            </Box>
          ))}

          <TablePagination
            component="div"
            count={totalResults}
            page={page}
            onPageChange={(e, p) => handleChangePage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  );
}
